/**
 * Types and helpers for blob-backed review sessions.
 *
 * When a project is shared from the embedded app, the edge function stores
 * a blob manifest in review_sessions.project_json instead of inline JSON.
 * The manifest contains Vercel Blob CDN URLs for the .previs file and assets.
 */

import type { TimelineProject } from '@/types/timeline.ts';

export interface BlobManifest {
  _type: 'blob-backed';
  teamProjectId: string;
  previs_url: string;
  thumbnail_urls: Array<{ shotId: number; url: string }> | null;
  video_urls: Array<{ shotId: number; url: string }> | null;
  audio_urls: Array<{ clipId: number; url: string }> | null;
  character_image_urls: Array<{ characterId: number; name: string; url: string }> | null;
}

/** Type guard: returns true if project_json is a blob manifest rather than inline JSON. */
export function isBlobManifest(value: unknown): value is BlobManifest {
  return (
    value != null &&
    typeof value === 'object' &&
    (value as any)._type === 'blob-backed' &&
    typeof (value as any).previs_url === 'string'
  );
}

/**
 * Apply CDN URLs from a blob manifest onto a loaded TimelineProject.
 * Mutates the project in place.
 *
 * Logic migrated from TeamProjectLoader.loadProjectData.
 */
export function applyBlobManifestUrls(project: TimelineProject, manifest: BlobManifest): void {
  // Thumbnails — skip shots with historyId (those have AI-enhanced thumbnails from generation history)
  if (manifest.thumbnail_urls?.length) {
    const thumbMap = new Map(manifest.thumbnail_urls.map(t => [t.shotId, t.url]));
    for (const scene of project.scenes) {
      for (const shot of scene.shots) {
        if (shot.historyId) continue;
        const blobUrl = thumbMap.get(shot.id);
        if (blobUrl) {
          shot.thumbnailDataUrl = blobUrl;
        }
      }
    }
  }

  // Videos — animatic shots only
  if (manifest.video_urls?.length) {
    const videoMap = new Map(manifest.video_urls.map(v => [v.shotId, v.url]));
    for (const scene of project.scenes) {
      for (const shot of scene.shots) {
        if (!shot.isAnimatic) continue;
        const videoUrl = videoMap.get(shot.id);
        if (videoUrl) {
          shot.videoObjectUrl = videoUrl;
        }
      }
    }
  }

  // Audio clips
  if (manifest.audio_urls?.length && project.audioClips?.length) {
    const audioMap = new Map(manifest.audio_urls.map(a => [a.clipId, a.url]));
    for (const clip of project.audioClips) {
      const blobUrl = audioMap.get(clip.id);
      if (blobUrl) {
        clip.audioFileUrl = blobUrl;
      }
    }
  }

  // Character headshot images
  if (manifest.character_image_urls?.length && project.screenplayCharacters?.length) {
    const charImageMap = new Map(manifest.character_image_urls.map(c => [c.characterId, c.url]));
    for (const char of project.screenplayCharacters) {
      const imageUrl = charImageMap.get(char.id);
      if (imageUrl) {
        char.imageUrl = imageUrl;
      }
    }
  }
}
